import { useRef, useEffect, useCallback } from 'react'

export const useExportAnimationCallback = ({
  isExportCopy,
  onChartReady,
  isPdfGenerating,
  signalComponentReady,
  componentKey
}) => {
  // Track if first render is complete to prevent multiple callback invocations
  // (Chart.js animation.onComplete fires multiple times - on hover, tooltips, etc.)
  const hasRenderedRef = useRef(false)
  const hasPdfSignaledRef = useRef(false)

  // Track previous export states to detect when export starts
  const prevIsPdfGeneratingRef = useRef(isPdfGenerating)
  const prevIsExportCopyRef = useRef(isExportCopy)

  // Reset refs when export mode becomes active (false -> true transition)
  useEffect(() => {
    if (!prevIsPdfGeneratingRef.current && isPdfGenerating) {
      hasPdfSignaledRef.current = false
    }
    if (!prevIsExportCopyRef.current && isExportCopy) {
      hasRenderedRef.current = false
    }
    prevIsPdfGeneratingRef.current = isPdfGenerating
    prevIsExportCopyRef.current = isExportCopy
  }, [isPdfGenerating, isExportCopy])

  // Called by Chart.js when animation completes
  // For PNG export: signals back to exportChartAsPng that chart is ready for screenshot
  // For PDF export: signals to PdfExportContext that chart has finished rendering
  const handleAnimationComplete = useCallback(() => {
    // Handle PNG export
    if (!hasRenderedRef.current && isExportCopy && onChartReady) {
      hasRenderedRef.current = true
      onChartReady()
    }

    // Handle PDF export (optional - only for charts that conditionally re-render during PDF export)
    if (signalComponentReady && componentKey && !hasPdfSignaledRef.current && isPdfGenerating && !isExportCopy) {
      hasPdfSignaledRef.current = true
      signalComponentReady(componentKey)
    }
  }, [isExportCopy, onChartReady, isPdfGenerating, signalComponentReady, componentKey])

  return handleAnimationComplete
}
